// frontend/src/pages/partner/DashboardPartner.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CiAlignBottom } from "react-icons/ci";
import { CiShoppingCart } from "react-icons/ci";
import { CiUser } from "react-icons/ci";
import { CiDatabase } from "react-icons/ci";
import { CiPaperplane } from "react-icons/ci";

import { CiBag1 } from "react-icons/ci";


import { motion } from 'framer-motion';
import API from '../../services/api';
import SalesEvolutionChart from '../../components/charts/SalesEvolutionChart';
import './DashboardPartner.css';

/* ── Animations ─────────────────────────────────────────────── */
const fadeUp = {
  hidden:  { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 24 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

/* ── Helpers ─────────────────────────────────────────────────── */
const fmt      = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtEur   = (n) => `${Number(n || 0).toFixed(2)} TND`;
const fmtPts   = (n) => `${fmt(n)} pts`;

/* ── Composant KPI Card ──────────────────────────────────────── */
const KpiCard = ({ icon, label, value, color = 'blue', sub }) => (
  <div className={`kpi-card kpi-card--${color}`}>
    <div className="kpi-card__icon">
      {icon}
    </div>
    <div className="kpi-card__body">
      <span className="kpi-card__label">{label}</span>
      <span className="kpi-card__value">{value}</span>
      {sub && <span className="kpi-card__sub">{sub}</span>}
    </div>
  </div>
);

/* ── Composant Section Header ────────────────────────────────── */
const SectionHeader = ({ icon, label, color }) => (
  <div className={`kpi-section-header kpi-section-header--${color}`}>
    <span className="kpi-section-header__icon"><i className={`fas ${icon}`} /></span>
    <span className="kpi-section-header__label">{label}</span>
    <div className="kpi-section-header__line" />
  </div>
);

/* ── Composant Progress Bar ──────────────────────────────────── */
const UsageBar = ({ used, total }) => {
  const pct = total > 0 ? Math.min(Math.round((used / total) * 100), 100) : 0;
  const color = pct >= 80 ? 'danger' : pct >= 50 ? 'warning' : 'success';

  return (
    <div className="usage-bar-wrapper">
      <div className="usage-bar-info">
        <span className="usage-bar-label">
          <i className="fas fa-chart-pie" /> Taux d'utilisation des points
        </span>
        <span className={`usage-bar-pct usage-bar-pct--${color}`}>{pct}%</span>
      </div>
      <div className="usage-bar-track">
        <motion.div
          className={`usage-bar-fill usage-bar-fill--${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        />
      </div>
      <div className="usage-bar-legend">
        <span><i className="fas fa-coins" /> {fmtPts(used)} vendus</span>
        <span><i className="fas fa-database" /> {fmtPts(total)} achetés</span>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   DASHBOARD PRINCIPAL
   ══════════════════════════════════════════════════════════════ */
const DashboardPartner = () => {
  const navigate = useNavigate();

  const [user,           setUser]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30j');
  const [periodData,     setPeriodData]     = useState([]);
  const [recentSales,    setRecentSales]    = useState([]);
  const [services,       setServices]       = useState([]);
  const [stats, setStats] = useState({
    revenue: 0, sales: 0, clients: 0,
    pointsStock: 0, pointsVendus: 0, pointsAchetes: 0,
    panierMoyen: 0, tauxUtilisation: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login/partner'); return; }
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
  }, [navigate]);

  useEffect(() => { fetchDashboard(); }, [selectedPeriod]); // eslint-disable-line

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [servicesRes, transactionsRes, pointsRes] = await Promise.all([
        API.get('/companies/my'),
        API.get('/transactions/history'),
        API.get('/transactions/company-points'),
      ]);

      const servicesData   = servicesRes.data.companies || [];
      const transactions   = transactionsRes.data.transactions || [];
      const companyPoints  = pointsRes.data.companyPoints || [];

      setServices(servicesData);

      /* ── Filtre période ─────────────────────────────────────── */
      const periodStart = new Date();
      if (selectedPeriod === '7j')  periodStart.setDate(periodStart.getDate() - 7);
      if (selectedPeriod === '30j') periodStart.setDate(periodStart.getDate() - 30);
      if (selectedPeriod === '90j') periodStart.setDate(periodStart.getDate() - 90);

      const periodTx = transactions.filter(t => new Date(t.created_at) >= periodStart);
      const sales    = periodTx.filter(t => t.transaction_type === 'sale' && t.status === 'completed');

      /* ── Métriques ──────────────────────────────────────────── */
      const totalRevenue     = sales.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
      const totalPointsSold  = sales.reduce((s, t) => s + (parseInt(t.points_amount) || 0), 0);
      const uniqueClients    = new Set(
        transactions
          .filter(t => t.transaction_type === 'sale' && t.status === 'completed')
          .map(t => t.client_id).filter(Boolean)
      ).size;

      const totalPointsStock = companyPoints.reduce((s, i) => s + (i.total_points || 0), 0);
      const panierMoyen      = sales.length > 0 ? totalRevenue / sales.length : 0;

      /* Points totaux achetés ET vendus (toutes périodes) pour le taux d'utilisation */
      const allCompletedSales = transactions.filter(t => t.transaction_type === 'sale' && t.status === 'completed');
      const totalPointsSoldAllTime = allCompletedSales.reduce((s, t) => s + (parseInt(t.points_amount) || 0), 0);

      const totalPointsAchetes = transactions
        .filter(t => t.transaction_type === 'purchase' && t.status === 'completed')
        .reduce((s, t) => s + (parseInt(t.points_amount) || 0), 0);

      const tauxUtilisation = totalPointsAchetes > 0
        ? Math.round((totalPointsSoldAllTime / totalPointsAchetes) * 100)
        : 0;

      setStats({
        revenue: totalRevenue,
        sales: sales.length,
        clients: uniqueClients,
        pointsStock: totalPointsStock,
        pointsVendus: totalPointsSold,
        pointsVendusAllTime: totalPointsSoldAllTime,
        pointsAchetes: totalPointsAchetes,
        panierMoyen,
        tauxUtilisation,
      });

      /* ── Dernières ventes ───────────────────────────────────── */
      const lastSales = transactions
        .filter(t => t.transaction_type === 'sale' && t.status === 'completed')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5)
        .map(t => ({
          id:      t.id,
          client:  t.client_first_name
            ? `${t.client_first_name} ${t.client_last_name || ''}`.trim()
            : 'Client',
          service: t.company_name || 'Service',
          points:  parseInt(t.points_amount) || 0,
          amount:  parseFloat(t.amount) || 0,
          date:    t.created_at,
        }));
      setRecentSales(lastSales);

      /* ── Données graphique (array ISO pour SalesEvolutionChart) ── */
      const dailyObj = {};
      sales.forEach(t => {
        const d = new Date(t.created_at).toISOString().split('T')[0];
        if (!dailyObj[d]) dailyObj[d] = { daily_revenue: 0, daily_sales: 0 };
        dailyObj[d].daily_revenue += parseFloat(t.amount) || 0;
        dailyObj[d].daily_sales   += 1;
      });
      const dailyArray = Object.entries(dailyObj)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, vals]) => ({ date, ...vals }));
      setPeriodData(dailyArray);

    } catch (err) {
      console.error('Erreur dashboard:', err);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader" />
        <p>Chargement du tableau de bord…</p>
      </div>
    );
  }

  return (
    <motion.div
      className="dashboard-partner"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >

      {/* ════════════════════════════════════════════════════════
          BANNER D'ACCUEIL
          ════════════════════════════════════════════════════════ */}
      <motion.div
        className="welcome-section"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1,  y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <div className="welcome-left">
          <h1 className="welcome-title">
            Bonjour, {user?.company_name || user?.first_name || 'Partenaire'} 👋
          </h1>
          <p className="welcome-date">
            <i className="far fa-calendar-alt" />
            {new Date().toLocaleDateString('fr-FR', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            })}
          </p>
        </div>

        <div className="welcome-actions">
          <button
            className="welcome-btn welcome-btn--primary"
            onClick={() => navigate('/dashboard/partner/purchase-packs')}
          >
            <i className="fas fa-plus" /> Acheter des points
          </button>
          <button
            className="welcome-btn welcome-btn--secondary"
            onClick={() => navigate('/dashboard/partner/services')}
          >
            <i className="fas fa-cube" /> Gérer les services
          </button>
        </div>
      </motion.div>

      {/* ════════════════════════════════════════════════════════
          KPIs — 3 GROUPES SÉMANTIQUES
          ════════════════════════════════════════════════════════ */}
      <motion.div
        className="kpi-groups"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >

        {/* ── 💰 Groupe 1 : Performance financière ── */}
        <motion.div className="kpi-group" variants={fadeUp}>
          <SectionHeader icon="fa-euro-sign" label="Performance financière" color="blue" />
          <div className="kpi-row kpi-row--2">
            <KpiCard
            icon={<CiAlignBottom size={30} />}
              label="Chiffre d'affaires"
              value={fmtEur(stats.revenue)}
              color="blue"
              sub={`sur les ${selectedPeriod}`}
            />
            <KpiCard
             icon={<CiBag1 size={30} />}
              label="Panier moyen"
              value={fmtEur(stats.panierMoyen)}
              color="blue"
              sub="par vente"
            />
          </div>
        </motion.div>

        {/* ── 📊 Groupe 2 : Activité ── */}
        <motion.div className="kpi-group" variants={fadeUp}>
          <SectionHeader icon="fa-chart-bar" label="Activité" color="green" />
          <div className="kpi-row kpi-row--2">
            <KpiCard
              icon={<CiShoppingCart size={30} />}
              label="Ventes"
              value={fmt(stats.sales)}
              color="green"
              sub={`sur les ${selectedPeriod}`}
            />
            <KpiCard
             icon={<CiUser size={30} />}
              label="Clients uniques"
              value={fmt(stats.clients)}
              color="green"
              sub="au total"
            />
          </div>
        </motion.div>

        {/* ── 🎯 Groupe 3 : Points ── */}
        <motion.div className="kpi-group" variants={fadeUp}>
          <SectionHeader icon="fa-coins" label="Points" color="amber" />
          <div className="kpi-row kpi-row--2">
            <KpiCard
             icon={<CiDatabase size={30} />}
              label="Points en stock"
              value={fmtPts(stats.pointsStock)}
              color="amber"
              sub="disponibles"
            />
            <KpiCard
             icon={<CiPaperplane size={30} />}
              label="Points vendus"
              value={fmtPts(stats.pointsVendus)}
              color="amber"
              sub={`sur les ${selectedPeriod}`}
            />
          </div>
          <UsageBar used={stats.pointsVendusAllTime} total={stats.pointsAchetes} />
        </motion.div>

      </motion.div>

      {/* ════════════════════════════════════════════════════════
          GRAPHIQUE + SERVICES
          ════════════════════════════════════════════════════════ */}
      <div className="dashboard-row">

        {/* Graphique */}
        <SalesEvolutionChart
          data={periodData}
          period={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
        />

        {/* Services */}
        <div className="services-card">
          <div className="card-header">
            <h3><i className="fas fa-cube" /> Mes services</h3>
            <button className="link-btn" onClick={() => navigate('/dashboard/partner/services')}>
              Voir tout <i className="fas fa-arrow-right" />
            </button>
          </div>
          <div className="services-list">
            {services.length === 0 ? (
              <div className="chart-empty">
                <i className="fas fa-cube" />
                <p>Aucun service créé</p>
                <button
                  className="empty-cta"
                  onClick={() => navigate('/dashboard/partner/services')}
                >
                  Créer un service
                </button>
              </div>
            ) : (
              services.slice(0, 4).map(s => (
                <div
                  key={s.id}
                  className="service-item"
                  onClick={() => navigate(`/dashboard/partner/b2c-packs?service=${s.id}`)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="service-icon"><i className="fas fa-cube" /></div>
                  <div className="service-info">
                    <span className="service-name">{s.name}</span>
                    <div className="service-stats-mini">
                      <span><i className="fas fa-coins" /> {fmt(s.stock?.total_points || 0)} pts</span>
                      <span><i className="fas fa-users" /> {s.clients_count || 0} clients</span>
                    </div>
                  </div>
                  <button className="service-action">
                    <i className="fas fa-chevron-right" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

      {/* ════════════════════════════════════════════════════════
          DERNIÈRES VENTES
          ════════════════════════════════════════════════════════ */}
      <div className="sales-card">
        <div className="card-header">
          <h3><i className="fas fa-history" /> Dernières ventes</h3>
          <button className="link-btn" onClick={() => navigate('/dashboard/partner/sales')}>
            Voir tout <i className="fas fa-arrow-right" />
          </button>
        </div>

        {recentSales.length === 0 ? (
          <div className="chart-empty">
            <i className="fas fa-shopping-cart" />
            <p>Aucune vente récente</p>
          </div>
        ) : (
          <div className="sales-table-wrapper">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Points</th>
                  <th>Montant</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentSales.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div className="table-user">
                        <div className="table-avatar">
                          {s.client[0]?.toUpperCase() || 'C'}
                        </div>
                        {s.client}
                      </div>
                    </td>
                    <td>
                      <span className="table-badge">{s.service}</span>
                    </td>
                    <td className="points-cell">{fmtPts(s.points)}</td>
                    <td className="amount-cell">{fmtEur(s.amount)}</td>
                    <td className="date-cell">
                      {new Date(s.date).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════
          ACTIONS RAPIDES
          ════════════════════════════════════════════════════════ */}
      <div className="quick-actions-card">
        <div className="card-header">
          <h3><i className="fas fa-bolt" /> Actions rapides</h3>
        </div>
        <div className="quick-actions-grid">
          <button
            className="qa-tile qa-tile--primary"
            onClick={() => navigate('/dashboard/partner/purchase-packs')}
          >
            <div className="qa-tile__icon"><i className="fas fa-cart-plus" /></div>
            <div className="qa-tile__body">
              <span className="qa-tile__label">Acheter des points</span>
              <span className="qa-tile__sub">Recharger votre wallet</span>
            </div>
            <i className="fas fa-arrow-right qa-tile__arrow" />
          </button>

          <button
            className="qa-tile qa-tile--secondary"
            onClick={() => navigate('/dashboard/partner/services')}
          >
            <div className="qa-tile__icon"><i className="fas fa-plus-circle" /></div>
            <div className="qa-tile__body">
              <span className="qa-tile__label">Nouveau service</span>
              <span className="qa-tile__sub">Créer et configurer</span>
            </div>
            <i className="fas fa-arrow-right qa-tile__arrow" />
          </button>

          <button
            className="qa-tile qa-tile--secondary"
            onClick={() => navigate('/dashboard/partner/api')}
          >
            <div className="qa-tile__icon"><i className="fas fa-key" /></div>
            <div className="qa-tile__body">
              <span className="qa-tile__label">Clés API</span>
              <span className="qa-tile__sub">Gérer l'intégration</span>
            </div>
            <i className="fas fa-arrow-right qa-tile__arrow" />
          </button>

          <button
            className="qa-tile qa-tile--secondary"
            onClick={() => navigate('/dashboard/partner/sales')}
          >
            <div className="qa-tile__icon"><i className="fas fa-download" /></div>
            <div className="qa-tile__body">
              <span className="qa-tile__label">Rapports</span>
              <span className="qa-tile__sub">Exporter les données</span>
            </div>
            <i className="fas fa-arrow-right qa-tile__arrow" />
          </button>
        </div>
      </div>

    </motion.div>
  );
};

export default DashboardPartner;
